/* PlannerCaseCommandHeader — iter101.59
 *
 * Case-detail header for the Planner portal. Data-only wrapper around
 * the shared /components/shared/CommandHeaderShell — the visual
 * grammar (teal wash, noise texture, zone divider, data-strip layout)
 * lives in the shell so Planner + Doctor + Patient headers share ONE
 * source of truth.
 *
 * What the planner sees:
 *   Zone A — identity + provider/clinic assignment stack + status pill
 *   Zone B — 6-cell chip strip:
 *     Case ID · Created · Planning · Manufacturing · IO Scans · Compliance
 *   Optional revision stripe when the plan has been through the
 *   doctor at least once.
 *
 * Props unchanged from iter101.52 — this file is a drop-in refactor.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
    Stethoscope, Buildings, Hash, CalendarBlank, PencilSimple,
    Sparkle, Timer, ScanSmiley, ShieldCheck, ArrowsClockwise,
} from "@phosphor-icons/react";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import CommandHeaderShell from "@/components/shared/CommandHeaderShell";


function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        }).replace(/\s/g, "-");
    } catch {
        return "—";
    }
}


export default function PlannerCaseCommandHeader({ caseObj, initials, openAllForPatient }) {
    if (!caseObj) return null;
    const c = caseObj;

    /* ── Derive data-strip values (mirrors CaseInfoChipRow logic). ── */
    const priorityCode = c.pricing?.priority_code || "standard";
    const priorityLabel = c.pricing?.priority_label || "Standard";
    const priorityTat = c.pricing?.priority_tat_days;
    const mfgValue = priorityCode === "standard"
        ? `Standard${priorityTat ? ` · ${priorityTat}d TAT` : ""}`
        : `${priorityLabel}${priorityTat ? ` · ${priorityTat}d TAT` : ""}`;

    const ioPay = c.pricing?.io_scanning_payment_choice;
    const ioFee = c.pricing?.scanner_pickup_fee;
    const hasIoScan = Boolean(ioPay)
        || (typeof ioFee === "number" && ioFee > 0)
        || Boolean(c.pricing?.scanner_pickup_requested);
    const ioValue = hasIoScan ? "Requested" : "Not requested";

    const complianceUpgraded = c.coe_support_compliance === true;
    const complianceValue = complianceUpgraded ? "Invisique™ manages" : "Self-managed";
    const complianceTone = complianceUpgraded ? "emerald" : "rose";

    const planningValue = c.planning_mode === "doctor" ? "Self-planned" : "Invisique™ Team";
    const revisionCount = c.revision_count || 0;
    const providerName = (c.doctor_name || "").replace(/^Dr\.\s*/, "");
    const clinicName = c.practice_location_name || c.clinic_name || "—";

    const demographics = [
        c.patient_age && `${c.patient_age} yr`,
        c.patient_gender && (c.patient_gender.charAt(0).toUpperCase() + c.patient_gender.slice(1)),
        c.patient_code,
    ].filter(Boolean);

    /* Avatar — inline so the planner variant can reuse it via the
       shell's `avatar` slot. Same double-ring teal treatment used
       previously; 64px so it matches the doctor portal's
       PatientProfile header. */
    const avatarNode = c.patient_photo_data_url ? (
        <div
            className="relative rounded-full ring-1 ring-teal-200/60 ring-offset-2 ring-offset-card"
            data-testid="planner-patient-avatar-wrap"
        >
            <img
                src={c.patient_photo_data_url}
                alt={c.patient_name || "Patient"}
                className="w-16 h-16 rounded-full object-cover border border-border/60 bg-secondary"
                data-testid="planner-patient-avatar-img"
                loading="lazy"
            />
        </div>
    ) : (
        <div
            className="relative w-16 h-16 rounded-full grid place-items-center text-[19px] font-bold ring-1 ring-teal-200/60 ring-offset-2 ring-offset-card"
            style={{
                background: "linear-gradient(135deg, rgba(20,184,166,0.14) 0%, rgba(20,184,166,0.06) 100%)",
                color: "rgb(15 118 110)",
            }}
            data-testid="planner-patient-avatar"
            aria-label={`Initials for ${c.patient_name || "patient"}`}
        >
            {initials}
        </div>
    );

    /* Provider avatar chip — small photo when available, teal-tinted
       stethoscope glyph otherwise. Fed into the assignment mini-stack. */
    const providerAvatar = c.doctor_image ? (
        <img
            src={c.doctor_image}
            alt=""
            className="w-6 h-6 rounded-full object-cover border border-border/60 shrink-0 ring-1 ring-teal-200/50"
            onError={(e) => { e.target.style.display = "none"; }}
            data-testid="planner-case-provider-avatar"
        />
    ) : (
        <span
            className="w-6 h-6 rounded-full grid place-items-center shrink-0 ring-1 ring-teal-200/50"
            style={{ background: "rgba(20,184,166,0.10)" }}
        >
            <Stethoscope size={12} weight="fill" className="text-teal-700/70" />
        </span>
    );

    const demographicsSuffix = openAllForPatient ? (
        <Link
            to={openAllForPatient}
            className="text-primary hover:underline font-semibold"
            data-testid="planner-case-open-all-for-patient"
        >
            Open all cases
        </Link>
    ) : null;

    const stripe = revisionCount > 0 ? (
        <div
            className="border-t border-amber-200/60 bg-amber-50/70 px-5 py-1.5 flex items-center gap-1.5"
            data-testid="planner-case-revision-stripe"
        >
            <ArrowsClockwise size={11} weight="fill" className="text-amber-700" />
            <span
                className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800"
                data-testid="case-chip-revision"
            >
                Revision {revisionCount}
            </span>
            <span className="text-[10px] text-amber-700/80 ml-1">
                · This plan has been revised {revisionCount} time{revisionCount === 1 ? "" : "s"} since first submission
            </span>
        </div>
    ) : null;

    return (
        <CommandHeaderShell
            testId="planner-case-command-header"
            avatar={avatarNode}
            identityLabel="Patient · Case"
            name={c.patient_name || "Unnamed patient"}
            nameTitle={c.patient_name}
            demographics={demographics}
            demographicsSuffix={demographicsSuffix}
            assignments={[
                {
                    label: "Provider",
                    value: `Dr. ${providerName}`,
                    avatar: providerAvatar,
                    testid: "planner-case-provider-name",
                    title: `Provider: Dr. ${providerName}`,
                },
                {
                    label: "Clinic",
                    value: clinicName,
                    icon: Buildings,
                    testid: "planner-case-clinic-name",
                    title: clinicName,
                },
            ]}
            rightSlot={null}
            statusPill={(
                <span data-testid="case-status-pill">
                    <CaseStatusBadge status={c.status} size="md" />
                </span>
            )}
            chips={[
                { icon: Hash, label: "Case ID", value: c.code || "—", mono: true, testid: "case-chip-code", title: c.code },
                { icon: CalendarBlank, label: "Created", value: fmtDate(c.created_at), testid: "case-chip-created" },
                {
                    icon: c.planning_mode === "doctor" ? PencilSimple : Sparkle,
                    label: "Planning",
                    value: planningValue,
                    testid: c.planning_mode === "doctor" ? "case-planning-mode-self" : "case-planning-mode-coe",
                    title: c.planning_mode === "doctor"
                        ? "The doctor authored the treatment plan on this case"
                        : "Invisique™ Planning Team authored the treatment plan",
                },
                {
                    icon: Timer,
                    label: "Manufacturing",
                    value: mfgValue,
                    testid: "case-chip-manufacturing",
                    title: priorityTat ? `Expected manufacturing turnaround: ${priorityTat} days` : "Manufacturing turnaround by tier",
                },
                {
                    icon: ScanSmiley,
                    label: "IO Scans",
                    value: ioValue,
                    testid: "case-chip-io-scan",
                    title: hasIoScan
                        ? "Invisique will send an IO-scanning partner to the clinic"
                        : "Doctor submits their own PVS impressions or intraoral scans",
                },
                {
                    icon: complianceUpgraded ? ShieldCheck : Stethoscope,
                    label: "Compliance",
                    value: complianceValue,
                    tone: complianceTone,
                    testid: complianceUpgraded ? "case-monitoring-mode-invisique" : "case-monitoring-mode-empty-readonly",
                    title: complianceUpgraded
                        ? "Invisique™ runs patient-side compliance & monitoring on this case"
                        : "Doctor is self-managing compliance on this case",
                },
            ]}
            stripe={stripe}
        />
    );
}
